import { formatValue, formatUnknownValue } from './formatValue';
import { findFieldByRef } from './fieldMatch';
import { printPageChromeCss, wrapPrintBody } from './printPage';
import { toRmbUpper } from './rmbUpper';
import type {
  ContractTemplate,
  ContractRenderData,
  ContractSection,
  HeaderSection,
  InfoGridSection,
  DetailTableSection,
  FreeTableRow,
  FreeTableSection,
  TextBlockSection,
  SignatureSection,
  DividerSection,
  AttachmentItem,
  AttachmentSection,
  CodeSection,
  RichTextSection,
  ColumnsSection,
} from './types';
import { renderRichTextValue } from './richText';
import { normalizeFreeTable } from './freeTable';
import { INSERTABLE_SECTION_TYPES, SECTION_TYPE_LABELS } from './types';

function esc(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** 编辑态的字段胶囊：一眼看出这里绑的是字段，不是写死的文字。 */
function fieldChip(name: string, ref = name): string {
  // data-chip 带原始引用（含 |大写 这类格式后缀），画布上点胶囊换字段时靠它定位
  return (
    `<span data-chip="${esc(ref)}" title="点击换成别的字段" style="display:inline-block;padding:0 5px;margin:0 1px;border:1px solid #c9b9a8;` +
    `border-radius:4px;background:#faf5ef;color:#8a6b52;font-size:0.92em;white-space:nowrap;cursor:pointer;">` +
    `{{${esc(name)}}}</span>`
  );
}

/** 返回值已经是 HTML（可能含胶囊）时用它，避免调用方再 esc 一次。 */
function resolveFieldHtml(ref: string, data: ContractRenderData): string {
  if (data.fieldChips) {
    const raw = ref.includes('|') ? ref.slice(0, ref.lastIndexOf('|')).trim() : ref.trim();
    // 胶囊上要显示字段**名**。信息网格和签章行绑的是字段 id，直接印出来
    // 是一串 fldXXXX，用户根本认不出是哪个字段。
    const name = findFieldByRef(data.mainFieldMetas, raw)?.name ?? raw;
    return fieldChip(name, ref);
  }
  return esc(resolveFieldRef(ref, data));
}

function resolveFieldRef(
  ref: string,
  data: ContractRenderData,
): string {
  // 支持格式化修饰符：{{字段名|大写}} → 人民币中文大写金额
  let modifier = '';
  let name = ref;
  const pipe = ref.lastIndexOf('|');
  if (pipe >= 0) {
    modifier = ref.slice(pipe + 1).trim();
    name = ref.slice(0, pipe).trim();
  }
  const matched = findFieldByRef(data.mainFieldMetas, name);
  const meta = matched ? data.mainFieldMetas.find((m) => m.id === matched.id) : undefined;
  const raw = meta ? data.mainRecord[meta.id] : data.mainRecord[name];
  const text = meta ? formatValue(meta, raw) : formatUnknownValue(raw);
  // 示例模式（未选中记录）：空值显示成〔字段名〕，否则整份文档看着像没生效
  if (data.sampleMode && !text) return `〔${meta?.name || name}〕`;
  if (modifier === '大写' || modifier === 'rmb') return toRmbUpper(text);
  return text;
}

function evalVisibility(rule: import('./types').VisibilityRule, data: ContractRenderData): boolean {
  // 示例模式没有真实值，按规则算全是"空"，会把配了条件的区块全藏掉 —— 一律放行
  if (data.sampleMode) return true;
  // 开了开关但还没选字段（fieldId 为空）、或字段已被删掉：规则没法判定，
  // 按「不生效」放行。以前按空值算，notEmpty 直接把整块藏掉，用户在编辑器里
  // 只看到区块变淡、在首页预览里整块消失，还以为是明细带不出来。
  if (!rule.fieldId) return true;
  const meta =
    data.mainFieldMetas.find((m) => m.id === rule.fieldId) ||
    data.mainFieldMetas.find((m) => m.name === rule.fieldId);
  if (!meta) return true;
  const val = formatValue(meta, data.mainRecord[meta.id]).trim();
  const cmp = (rule.value ?? '').trim();
  switch (rule.op) {
    case 'notEmpty': return val !== '';
    case 'empty': return val === '';
    case 'eq': return val === cmp;
    case 'ne': return val !== cmp;
    case 'contains': return cmp === '' || val.includes(cmp);
    default: return true;
  }
}

function interpolate(text: string, data: ContractRenderData): string {
  return text.replace(/\{\{([^}]+)\}\}/g, (_, fieldId: string) => resolveFieldHtml(fieldId, data));
}

/**
 * 单元格文本：字面量转义（不像文本段落那样放行 HTML —— 表格结构是我们自己
 * 生成的，用户手写的标签只会把 td 撑破），{{字段名}} 仍然替换，换行转 <br>。
 */
function renderCellText(text: string, data: ContractRenderData): string {
  return text
    .split(/(\{\{[^}]+\}\})/g)
    .map((part) => {
      const m = /^\{\{([^}]+)\}\}$/.exec(part);
      if (!m) return esc(part);
      // 引用的是附件字段且取到了图：直接排成图片，撑满格子（合并区越大图越大）
      const imgs = resolveFieldImages(m[1], data);
      if (imgs) return imgs;
      return resolveFieldHtml(m[1], data);
    })
    .join('')
    .replace(/\r?\n/g, '<br />');
}

/**
 * {{附件字段}} 在表格格子里的图片形态。宽度撑满格子、高度按比例，所以把
 * 图片格向下合并几行，图就跟着放大 —— 还原 Excel 单据里「左边一张大图，
 * 右边一列标签」的排法。非图片附件、示例模式、没取到 URL 时返回 null，
 * 由调用方退回文本路径。
 */
function resolveFieldImages(ref: string, data: ContractRenderData): string | null {
  if (data.sampleMode || !data.mainAttachments) return null;
  const name = ref.includes('|') ? ref.slice(0, ref.lastIndexOf('|')).trim() : ref.trim();
  const matched = findFieldByRef(data.mainFieldMetas, name);
  if (!matched) return null;
  const items = data.mainAttachments[matched.id];
  if (!items || items.length === 0) return null;
  const images = items.filter((it) => it.url && isImageMime(it.mimeType, it.name));
  if (images.length === 0) return null;
  return images
    .map(
      (it) =>
        `<img src="${esc(it.url)}"${imgFallback(it)} alt="${esc(it.name)}" style="width:100%;max-width:100%;object-fit:contain;display:block;margin:0 auto;" />`,
    )
    .join('');
}

/**
 * 整块文字被一个 span 包住时，去掉那个 span 上的字号 / 粗细。
 *
 * 就地编辑时浏览器会给文字补一层和原来一样的内联字号（全选重打、在带字号的
 * 块里输入都会）。留着它，之后在属性栏改块字号只改到容器：框跟着行高变了，
 * 字自己卡在内联的那个值上 —— 用户看到的就是「框变大字不变」。
 * 整块级别的样式归属性栏管；只有包住一部分文字的 span 才是用户刻意的局部样式。
 */
/** 整块被一个 span 包住时，读出它的字号 / 粗细（提交时提升成区块属性），并返回去掉它们后的内容。 */
export function extractWholeWrapStyles(content: string): { content: string; fontSizePx?: number; bold?: boolean } {
  const m = /^\s*<span style="([^"]*)">([\s\S]*)<\/span>\s*$/.exec(String(content ?? ''));
  if (!m) return { content };
  let depth = 0;
  for (const tag of m[2].match(/<\/?span\b[^>]*>/g) ?? []) {
    depth += tag.startsWith('</') ? -1 : 1;
    if (depth < 0) return { content };
  }
  if (depth !== 0) return { content };
  const out: { content: string; fontSizePx?: number; bold?: boolean } = { content: stripWholeWrapStyles(content) };
  const size = /font-size\s*:\s*([\d.]+)px/i.exec(m[1]);
  if (size) out.fontSizePx = Math.round(parseFloat(size[1]));
  const weight = /font-weight\s*:\s*(\d+|bold|normal)/i.exec(m[1]);
  if (weight) out.bold = weight[1] === 'bold' || weight[1] === '700' || Number(weight[1]) >= 600;
  return out;
}

export function stripWholeWrapStyles(content: string): string {
  let cur = String(content ?? '');
  for (let i = 0; i < 3; i++) {
    const m = /^\s*<span style="([^"]*)">([\s\S]*)<\/span>\s*$/.exec(cur);
    if (!m) break;
    // 里面的 span 必须自己配平：出现「先闭合再新开」说明外层那个 span 只包了前半截
    const inner = m[2];
    let depth = 0;
    let balanced = true;
    for (const tag of inner.match(/<\/?span\b[^>]*>/g) ?? []) {
      depth += tag.startsWith('</') ? -1 : 1;
      if (depth < 0) { balanced = false; break; }
    }
    if (!balanced || depth !== 0) break;
    const kept = m[1]
      .split(';')
      .map((d) => d.trim())
      .filter((d) => d && !/^(font-size|font-weight)\s*:/i.test(d))
      .join('; ');
    cur = kept ? `<span style="${kept}">${inner}</span>` : inner;
    if (kept) break;
  }
  return cur;
}

function renderHeader(s: HeaderSection, data: ContractRenderData): string {
  const fontSize = s.fontSizePx ? `font-size:${s.fontSizePx}px;` : '';
  const bold = s.bold ? 'font-weight:700;' : '';
  const content = interpolate(stripWholeWrapStyles(s.content), data);
  return `<div style="text-align:${s.align};${fontSize}${bold}margin-bottom:8px;white-space:pre-wrap;">${content}</div>`;
}

function renderInfoGrid(s: InfoGridSection, data: ContractRenderData): string {
  // columns 为 0 / null（AI 生成或手改过的模板）会让下面的 i += cols 永远不前进，
  // 侧边栏直接卡死、渲染服务 CPU 超时。兜到默认的 2 列。
  const cols = s.columns === 1 || s.columns === 2 || s.columns === 3 ? s.columns : 2;
  const rows: string[] = [];
  for (let i = 0; i < s.items.length; i += cols) {
    const cells: string[] = [];
    for (let j = 0; j < cols; j++) {
      const item = s.items[i + j];
      if (!item) {
        cells.push('<td></td><td></td>');
        continue;
      }
      const value = item.fieldId
        ? resolveFieldHtml(item.fieldId, data)
        : esc(item.staticValue);
      cells.push(
        `<td style="font-weight:600;padding:4px 8px;border:1px solid #999;white-space:nowrap;">${esc(item.label)}</td>` +
        `<td style="padding:4px 8px;border:1px solid #999;">${value}</td>`,
      );
    }
    rows.push(`<tr>${cells.join('')}</tr>`);
  }
  return `<table style="width:100%;border-collapse:collapse;margin-bottom:12px;">${rows.join('')}</table>`;
}

/** 示例模式的明细表骨架：表头 + 两行占位，让编辑者看得到列宽/对齐。 */
function renderDetailTableSkeleton(s: DetailTableSection): string {
  const head: string[] = [];
  if (s.showIndex) head.push('<th style="width:36px;text-align:center;">#</th>');
  for (const col of s.columns) {
    const width = col.width ? `width:${col.width};` : '';
    head.push(`<th style="text-align:${col.align || 'left'};${width}">${esc(col.label)}</th>`);
  }
  const row = (i: number) => {
    const cells: string[] = [];
    if (s.showIndex) cells.push(`<td style="text-align:center;color:#bbb;">${i}</td>`);
    for (const col of s.columns) {
      cells.push(`<td style="text-align:${col.align || 'left'};color:#bbb;">〔${esc(col.label)}〕</td>`);
    }
    return `<tr>${cells.join('')}</tr>`;
  };
  return `<table style="width:100%;border-collapse:collapse;margin-bottom:12px;">
<thead><tr style="background:#f5f5f5;font-weight:600;">${head.join('')}</tr></thead>
<tbody>${row(1)}${row(2)}</tbody>
</table>`;
}

/**
 * 把一个已格式化的单元格文本累加成数字。
 *
 * 多值的查找引用会格式化成「12, 15」，早先直接剥掉非数字字符会得到 1215，
 * 汇总行凭空翻十倍。这里按分隔符拆开逐项相加。
 */
function sumNumeric(text: string): number {
  let total = 0;
  for (const part of text.split(/[,，、;；\s]+/)) {
    if (!part) continue;
    // 千分位逗号已经被上面拆掉了，这里只需去掉货币符号等杂字符
    const n = Number(part.replace(/[^0-9.-]/g, ''));
    if (Number.isFinite(n)) total += n;
  }
  return total;
}

function renderDetailTable(
  s: DetailTableSection,
  data: ContractRenderData,
): string {
  // 按区块 id 取。以前按 linkFieldId 取，同一个关联字段配两张明细表时
  // 后一张会把前一张的数据覆盖掉（列还不一样，前一张整片空白）。
  // 回落到 linkFieldId 是为了兼容旧的取数实现和历史数据。
  const linked = data.linkedRecords[s.id] ?? data.linkedRecords[s.linkFieldId];
  if (!linked) {
    if (data.sampleMode) return renderDetailTableSkeleton(s);
    return '<p style="color:#999;">(无关联记录)</p>';
  }
  const metaById = new Map(linked.fieldMetas.map((m) => [m.id, m]));

  const headerCells: string[] = [];
  if (s.showIndex) headerCells.push('<th style="width:36px;text-align:center;">#</th>');
  for (const col of s.columns) {
    const align = col.align || 'left';
    const width = col.width ? `width:${col.width};` : '';
    headerCells.push(`<th style="text-align:${align};${width}">${esc(col.label)}</th>`);
  }

  // 明细筛选：只保留满足条件的行
  let records = linked.records;
  // value 可能是 undefined（老模板 / 自动化路线传来的半截筛选条件）——
  // 以前直接 .trim() 会抛错，整份文档渲染失败，而不只是这张表
  const filterCmp = String(s.filter?.value ?? '').trim();
  if (s.filter?.columnId && filterCmp !== '') {
    const col = s.columns.find((c) => c.id === s.filter!.columnId);
    if (col) {
      const meta = metaById.get(col.fieldId);
      const cmp = filterCmp;
      const ncmp = Number(cmp);
      records = records.filter((rec) => {
        const raw = rec.cells[col.fieldId];
        const v = (meta ? formatValue(meta, raw) : formatUnknownValue(raw)).trim();
        const nv = Number(v);
        switch (s.filter!.op) {
          case 'eq': return v === cmp;
          case 'ne': return v !== cmp;
          case 'contains': return v.includes(cmp);
          case 'gt': return !Number.isNaN(nv) && !Number.isNaN(ncmp) && nv > ncmp;
          case 'lt': return !Number.isNaN(nv) && !Number.isNaN(ncmp) && nv < ncmp;
          default: return true;
        }
      });
    }
  }

  // 明细排序：按指定列升/降序（数值优先，否则按中文文本）
  if (s.sort?.columnId) {
    const col = s.columns.find((c) => c.id === s.sort!.columnId);
    if (col) {
      const meta = metaById.get(col.fieldId);
      const dir = s.sort.dir === 'desc' ? -1 : 1;
      const key = (rec: (typeof records)[number]) => {
        const raw = rec.cells[col.fieldId];
        return meta ? formatValue(meta, raw) : formatUnknownValue(raw);
      };
      records = [...records].sort((a, b) => {
        const sa = key(a);
        const sb = key(b);
        const na = Number(sa);
        const nb = Number(sb);
        if (!Number.isNaN(na) && !Number.isNaN(nb) && sa !== '' && sb !== '') return (na - nb) * dir;
        return sa.localeCompare(sb, 'zh-CN') * dir;
      });
    }
  }

  const bodyRows: string[] = [];
  for (let i = 0; i < records.length; i++) {
    const rec = records[i];
    const cells: string[] = [];
    if (s.showIndex) cells.push(`<td style="text-align:center;">${i + 1}</td>`);
    for (const col of s.columns) {
      const meta = metaById.get(col.fieldId);
      const raw = rec.cells[col.fieldId];
      const align = col.align || 'left';
      const atts = rec.attachments?.[col.fieldId];
      if (atts && atts.length > 0) {
        cells.push(`<td style="text-align:${align};">${renderDetailCellAttachments(atts)}</td>`);
        continue;
      }
      const text = meta ? formatValue(meta, raw) : formatUnknownValue(raw);
      cells.push(`<td style="text-align:${align};">${esc(text)}</td>`);
    }
    bodyRows.push(`<tr>${cells.join('')}</tr>`);
  }

  if (s.summaryRow) {
    const sumSet = new Set(s.summaryRow.sumColumns);
    const cells: string[] = [];
    // 第一列自己就是求和列时，标签没地方放会整个消失 —— 优先塞进序号列，
    // 没有序号列就跟金额挤在同一格（总比印出一行没有名目的数字强）
    const labelInFirstCol = s.columns.length > 0 && !sumSet.has(s.columns[0].id);
    const labelInIndexCol = !labelInFirstCol && s.showIndex;
    let pendingLabel = labelInFirstCol || labelInIndexCol ? '' : s.summaryRow.label;
    if (s.showIndex) {
      const indexText = labelInIndexCol ? esc(s.summaryRow.label) : '&nbsp;';
      cells.push(`<td style="text-align:center;font-weight:700;">${indexText}</td>`);
    }
    for (let ci = 0; ci < s.columns.length; ci++) {
      const col = s.columns[ci];
      if (ci === 0 && labelInFirstCol) {
        cells.push(`<td style="font-weight:700;">${esc(s.summaryRow.label)}</td>`);
        continue;
      }
      if (sumSet.has(col.id)) {
        let sum = 0;
        for (const rec of records) {
          const meta = metaById.get(col.fieldId);
          const raw = rec.cells[col.fieldId];
          sum += sumNumeric(meta ? formatValue(meta, raw) : formatUnknownValue(raw));
        }
        const align = col.align || 'left';
        // 0.165*5000 这类金额连加会带出 4692.744000000001 的浮点尾巴，按 6 位小数收口
        const shown = String(Number(sum.toFixed(6)));
        const prefix = pendingLabel ? `${esc(pendingLabel)} ` : '';
        pendingLabel = '';
        cells.push(`<td style="text-align:${align};font-weight:700;">${prefix}${esc(shown)}</td>`);
      } else {
        cells.push('<td></td>');
      }
    }
    bodyRows.push(`<tr style="border-top:2px solid #333;">${cells.join('')}</tr>`);
  }

  return `<table style="width:100%;border-collapse:collapse;margin-bottom:12px;">
<thead><tr style="background:#f5f5f5;font-weight:600;">${headerCells.join('')}</tr></thead>
<tbody>${bodyRows.join('')}</tbody>
</table>`;
}

/** 明细表格里附件列的缩略图高度（mm）。单据行高有限，做成固定尺寸的小图。 */
const DETAIL_THUMB_MM = 20;

/**
 * 明细表格的附件格：图片内嵌成缩略图，非图片附件仍打印文件名。
 * 和附件图片区块一样走代理 URL + onerror 退回直连（见 imgFallback）。
 */
function renderDetailCellAttachments(items: AttachmentItem[]): string {
  const parts: string[] = [];
  for (const it of items) {
    if (it.url && isImageMime(it.mimeType, it.name)) {
      parts.push(
        `<img src="${esc(it.url)}"${imgFallback(it)} alt="${esc(it.name)}" style="max-height:${DETAIL_THUMB_MM}mm;max-width:100%;object-fit:contain;display:block;margin:2px auto;" />`,
      );
    } else {
      parts.push(`<div style="word-break:break-all;">${esc(it.name)}</div>`);
    }
  }
  return parts.join('');
}

/**
 * 样板行按关联子表展开：一条子记录复制一行。
 *
 * 复制出来的行不再带 rowSpan（跨行的样板行没有意义，会把后面的行吃掉），
 * colSpan 保留 —— 明细行里合并两列是常见排法。每格按 columnFields 的配置
 * 换成子表字段值；没配的列保持样板里的静态内容。
 */
interface ExpandedRow {
  row: FreeTableRow;
  /** 对应模板里第几行；null = 按数据展开出来的行，不能就地编辑或合并。 */
  srcRow: number | null;
  /**
   * 编辑态下第一行明细代表样板行：画布上能选、能右键、能绑列，改动落到模板。
   * 这时 row 里是数据值，握把上的原始内容得从这一行（模板的样板行）取。
   */
  raw?: FreeTableRow;
}

/** 明细表某一列写它 = 自动序号，而不是绑子表字段。 */
export const INDEX_REF = '__index__';

function expandRepeatRows(
  t: FreeTableSection,
  data: ContractRenderData,
  mode: RenderMode = 'output',
): { rows: ExpandedRow[]; note: string | null } {
  const rep = t.repeat;
  // 只有「关联子表」这种来源才必须有关联字段；「本表多条记录」没有关联字段，
  // 取数层直接按区块 id 归档了记录。
  const asSrc = (rows: FreeTableRow[]): ExpandedRow[] =>
    rows.map((row, i) => ({ row, srcRow: i }));
  if (!rep) return { rows: asSrc(t.rows), note: null };
  if ((rep.source ?? 'link') === 'link' && !rep.linkFieldId) {
    return { rows: asSrc(t.rows), note: null };
  }
  const idx = rep.rowIndex;
  const template = t.rows[idx];
  if (!template) return { rows: asSrc(t.rows), note: null };

  // 三种数据源都归档在 linkedRecords[区块 id] 下，取数层已经抹平差异
  const linked = data.linkedRecords[t.id] ?? data.linkedRecords[rep.linkFieldId];
  if (!linked) {
    // 示例模式（没选记录）和编辑态保留样板行本身，让编辑者看得见版式、选得到格子
    if (data.sampleMode || mode === 'edit') return { rows: asSrc(t.rows), note: null };
    return {
      rows: asSrc(t.rows).filter((x) => x.srcRow !== idx),
      note: null,
    };
  }
  const metaById = new Map(linked.fieldMetas.map((m) => [m.id, m]));
  const cap = Math.max(1, rep.maxRows ?? 500);
  const records = linked.records.slice(0, cap);
  const note =
    linked.records.length > cap ? `（明细共 ${linked.records.length} 行，只印前 ${cap} 行）` : null;

  const made: FreeTableRow[] = records.map((rec, n) => {
    // 斑马纹是单元格底纹（行上没有这个属性），只给动态行，
    // 静态的表头和合计行不参与，否则整表都在闪
    const stripe = !!t.zebra && n % 2 === 1;
    return {
      ...template,
      id: `${template.id}__r${n}`,
      cells: template.cells.map((cell, ci) => {
        const base = { ...cell, rowSpan: 1, shaded: stripe || cell.shaded };
        const ref = rep.columnFields[ci];
        if (!ref) return base;
        // 序号列：不绑字段，按展开顺序自动 1、2、3…
        if (ref === INDEX_REF) return { ...base, content: String(n + 1) };
        const meta = findFieldByRef(linked.fieldMetas, ref);
        const raw = meta ? rec.cells[meta.id] : undefined;
        const full = meta ? metaById.get(meta.id) : undefined;
        return { ...base, content: full ? formatValue(full, raw) : formatUnknownValue(raw) };
      }),
    };
  });

  // 合计行：对指定列把动态行的数值加起来
  const extras: FreeTableRow[] = [];
  if (t.summary && t.summary.columns.length > 0) {
    const sums = new Map<number, number>();
    for (const row of made) {
      for (const ci of t.summary.columns) {
        // 序号列是编号不是数量，不能加
        if (rep.columnFields[ci] === INDEX_REF) continue;
        sums.set(ci, (sums.get(ci) ?? 0) + sumNumeric(row.cells[ci]?.content ?? ''));
      }
    }
    let labelPlaced = false;
    extras.push({
      ...template,
      id: `${template.id}__sum`,
      cells: template.cells.map((cell, ci) => {
        if (t.summary!.columns.includes(ci) && rep.columnFields[ci] !== INDEX_REF) {
          return {
            ...cell,
            rowSpan: 1,
            shaded: false,
            bold: true,
            content: String(Number((sums.get(ci) ?? 0).toFixed(6))),
          };
        }
        // 标签放在第一个非求和列，放不下就算了（总比印出一行无名目的数字强）
        if (!labelPlaced) {
          labelPlaced = true;
          return { ...cell, rowSpan: 1, shaded: false, bold: true, content: t.summary!.label || '合计' };
        }
        return { ...cell, rowSpan: 1, shaded: false, content: '' };
      }),
    });
  }
  // 「共 N 条」：跨满整行，一眼看出明细有几行
  if (t.showCount) {
    extras.push({
      ...template,
      id: `${template.id}__count`,
      cells: template.cells.map((cell, ci) =>
        ci === 0
          ? { ...cell, rowSpan: 1, shaded: false, colSpan: template.cells.length, content: `共 ${records.length} 条`, align: 'left' as const, bold: false }
          : { ...cell, rowSpan: 1, shaded: false, colSpan: 1, merged: true },
      ),
    });
  }
  return {
    rows: [
      ...t.rows.slice(0, idx).map((row, i) => ({ row, srcRow: i })),
      // 展开出来的行和合计/计数行都不是作者写的，不给编辑入口 —— 除了编辑态下
      // 第一行明细：它代表样板行，否则有数据时样板行被顶掉，画布上没格子可选、
      // 左栏点子表字段也没地方落
      ...made.map((row, n) =>
        mode === 'edit' && n === 0 ? { row, srcRow: idx, raw: template } : { row, srcRow: null },
      ),
      ...extras.map((row) => ({ row, srcRow: null })),
      ...t.rows.slice(idx + 1).map((row, i) => ({ row, srcRow: idx + 1 + i })),
    ],
    note,
  };
}

function renderFreeTable(s: FreeTableSection, data: ContractRenderData, mode: RenderMode): string {
  // 样板行不能跨行：它要被复制成 N 行，跨行会把下面的合计行一并吃掉。
  // 必须在归一之前压平，否则 normalizeFreeTable 已经把下面几行标成占位格了。
  const src =
    s.repeat && s.rows[s.repeat.rowIndex]
      ? {
          ...s,
          rows: s.rows.map((row, i) =>
            i === s.repeat!.rowIndex
              ? { ...row, cells: row.cells.map((c) => (c.rowSpan > 1 ? { ...c, rowSpan: 1 } : c)) }
              : row,
          ),
        }
      : s;
  // 存量模板可能有越界的 span / 缺失的占位格，渲染前先归一，免得 td 数量对不上
  const t = normalizeFreeTable(src);
  const C = t.colWidths.length;
  if (C === 0 || t.rows.length === 0) return '';
  const border = t.borderPx > 0 ? `${t.borderPx}px solid #000` : 'none';
  const colgroup = `<colgroup>${t.colWidths
    .map((w) => `<col style="width:${w}%;" />`)
    .join('')}</colgroup>`;
  // 动态明细：样板行按子表条数展开。合并、跨行和明细三件事因此能同时成立。
  const { rows: bodyRows, note: overflowNote } = expandRepeatRows(t, data, mode);
  const rows = bodyRows
    .map(({ row, srcRow, raw }) => {
      const cells = row.cells
        .map((cell, ci) => ({ cell, ci }))
        .filter(({ cell }) => !cell.merged)
        .map(({ cell, ci }) => {
          const span =
            (cell.colSpan > 1 ? ` colspan="${cell.colSpan}"` : '') +
            (cell.rowSpan > 1 ? ` rowspan="${cell.rowSpan}"` : '');
          const style =
            `border:${border};padding:4px 6px;vertical-align:middle;text-align:${cell.align};` +
            (cell.bold ? 'font-weight:700;' : '') +
            (cell.bg ? `background:${esc(cell.bg)};` : cell.shaded ? 'background:#f2f2f2;' : '') +
            (cell.color ? `color:${esc(cell.color)};` : '') +
            (cell.fontSizePx ? `font-size:${Number(cell.fontSizePx)}px;` : '');
          // 编辑态给「作者写的那些行」挂上坐标：画布上右键合并、双击改字都靠它。
          // 按数据展开出来的行没有坐标 —— 它们不是模板的一部分，改了也存不回去。
          const handle =
            mode === 'edit' && srcRow !== null
              ? ` data-cell="1" data-sec="${esc(s.id)}" data-r="${srcRow}" data-c="${ci}"` +
                ` data-raw="${esc((raw ?? row).cells[ci]?.content ?? cell.content)}"`
              : '';
          const body = renderCellText(cell.content, data) || '&nbsp;';
          return `<td${span}${handle} style="${style}">${body}</td>`;
        })
        .join('');
      const h = row.heightMm ? ` style="height:${row.heightMm}mm;"` : '';
      return `<tr${h}>${cells}</tr>`;
    })
    .join('');
  const fs = t.fontSizePx ? `font-size:${t.fontSizePx}px;` : '';
  const tail = overflowNote
    ? `<div style="font-size:11px;color:#999;margin:-8px 0 12px;">${esc(overflowNote)}</div>`
    : '';
  return `<table style="width:100%;border-collapse:collapse;table-layout:fixed;margin-bottom:12px;${fs}">${colgroup}${rows}</table>${tail}`;
}

function renderTextBlock(s: TextBlockSection, data: ContractRenderData): string {
  const fontSize = s.fontSizePx ? `font-size:${s.fontSizePx}px;` : '';
  const align = s.align && s.align !== 'left' ? `text-align:${s.align};` : '';
  const content = interpolate(stripWholeWrapStyles(s.content), data);
  return `<div style="${fontSize}${align}margin-bottom:8px;white-space:pre-wrap;">${content}</div>`;
}

function renderSignature(s: SignatureSection, data: ContractRenderData): string {
  const width = s.align === 'right' && s.parties.length === 1 ? 50 : Math.floor(100 / s.parties.length);
  const partyCells = s.parties.map((party) => {
    const rows = party.rows.map((r) => {
      const value = r.fieldId
        ? resolveFieldHtml(r.fieldId, data)
        : esc(r.staticValue);
      return `<tr>
        <td style="font-weight:600;padding:6px 4px;white-space:nowrap;">${esc(r.label)}</td>
        <td style="padding:6px 4px;border-bottom:1px solid #ccc;min-width:120px;">${value}</td>
      </tr>`;
    });
    // 印章：40mm 实务尺寸压在签字栏右上，multiply 混合让章下文字透出来
    const sealMm = Number(party.sealSizeMm) > 0 ? Number(party.sealSizeMm) : 40;
    const seal = party.sealDataUrl
      ? `<img src="${esc(party.sealDataUrl)}" alt="印章" style="position:absolute;top:-6mm;right:2mm;width:${sealMm}mm;height:${sealMm}mm;object-fit:contain;mix-blend-mode:multiply;opacity:0.92;pointer-events:none;" />`
      : '';
    return `<td style="width:${width}%;vertical-align:top;padding:8px;">
      <div style="position:relative;">
        <div style="font-weight:700;margin-bottom:8px;">${esc(party.label)}</div>
        <table style="border-collapse:collapse;">${rows.join('')}</table>
        ${seal}
      </div>
    </td>`;
  });
  // 单方落款靠右：左边垫一格空的，落款和章就落在右半边
  const pad = s.align === 'right' && s.parties.length === 1 ? '<td style="width:50%;"></td>' : '';
  return `<table class="pp-sig" style="width:100%;margin-top:24px;"><tr>${pad}${partyCells.join('')}</tr></table>`;
}

/** 示例模式下"这里将来会有内容"的虚线占位块。 */
function samplePlaceholder(label: string, heightPx = 90): string {
  return `<div style="border:1px dashed #d0d0d0;border-radius:3px;height:${Math.round(heightPx)}px;display:flex;align-items:center;justify-content:center;color:#bbb;font-size:12px;margin:6px 0;">〔${esc(label)}〕</div>`;
}

function isImageMime(mimeType: string, name: string): boolean {
  if (mimeType.startsWith('image/')) return true;
  // 部分附件 cell 的 type 为空，按扩展名兜底
  return /\.(png|jpe?g|gif|webp|bmp|heic|svg)$/i.test(name);
}

/**
 * 图片加载失败时自动退回原始飞书链接。
 *
 * 首选地址是我们自己的图片代理（能绕开临时 URL 过期、第三方 Cookie 被拦、
 * CDN 按 referrer 拒绝这三种情况）。但代理也可能失手 —— 比如飞书换了个不在
 * 白名单里的域名。真失手了就退回浏览器直连，也就是加代理之前的行为，保证
 * 「修不好也不会更糟」。onerror 里先清掉自己，避免退回地址再失败时死循环。
 */
function imgFallback(it: { fallbackUrl?: string }): string {
  if (!it.fallbackUrl) return '';
  return ` onerror="this.onerror=null;this.src='${esc(it.fallbackUrl).replace(/'/g, '&#39;')}'"`;
}

function renderAttachment(s: AttachmentSection, data: ContractRenderData): string {
  const items = data.attachments[s.id] || [];
  const parts: string[] = [];
  if (s.title) {
    parts.push(`<div style="font-weight:700;margin:12px 0 8px;">${esc(s.title)}</div>`);
  }
  if (!s.fieldId) {
    return parts.join('') + '<p style="color:#999;">(请在区块设置里选择附件字段)</p>';
  }
  if (items.length === 0) {
    if (data.sampleMode) return parts.join('') + samplePlaceholder('附件图片', s.maxHeightMm ? s.maxHeightMm * 3.78 : 120);
    return parts.join('') + '<p style="color:#999;">(该记录没有附件)</p>';
  }

  const images = items.filter((it) => it.url && isImageMime(it.mimeType, it.name));
  const others = items.filter((it) => !it.url || !isImageMime(it.mimeType, it.name));

  const maxH = s.maxHeightMm ? `max-height:${s.maxHeightMm}mm;` : '';
  const caption = (name: string) =>
    s.showName
      ? `<div style="font-size:11px;color:#666;margin-top:2px;text-align:center;word-break:break-all;">${esc(name)}</div>`
      : '';

  if (s.layout === 'grid2') {
    const cells = images.map(
      (it) => `<div style="width:48%;page-break-inside:avoid;margin-bottom:8px;">
<img src="${esc(it.url)}"${imgFallback(it)} alt="${esc(it.name)}" style="width:100%;${maxH}object-fit:contain;display:block;border:1px solid #eee;" />
${caption(it.name)}
</div>`,
    );
    parts.push(
      `<div style="display:flex;flex-wrap:wrap;justify-content:space-between;">${cells.join('')}</div>`,
    );
  } else {
    for (const it of images) {
      const brk = s.pageBreakEach ? 'page-break-before:always;' : '';
      parts.push(`<div style="${brk}page-break-inside:avoid;margin-bottom:10px;text-align:center;">
<img src="${esc(it.url)}"${imgFallback(it)} alt="${esc(it.name)}" style="max-width:100%;${maxH}object-fit:contain;" />
${caption(it.name)}
</div>`);
    }
  }

  // 非图片附件（pdf/word 等）无法内嵌，列文件名说明随附
  if (others.length > 0) {
    const names = others.map((it) => `<li>${esc(it.name)}</li>`).join('');
    parts.push(
      `<div style="font-size:12px;color:#666;margin-top:4px;">其他附件（不支持内嵌打印）：<ul style="margin:2px 0 0 18px;padding:0;">${names}</ul></div>`,
    );
  }

  return parts.join('\n');
}

function renderCode(s: CodeSection, data: ContractRenderData): string {
  const src = data.codes?.[s.id];
  const align = s.align || 'left';
  if (!src) {
    if (data.sampleMode) {
      const side = s.codeType === 'qr' ? Math.max(80, s.sizePx || 120) : Math.max(30, s.sizePx || 60);
      return `<div style="text-align:${align};margin:8px 0;"><div style="display:inline-block;width:${side}px;height:${side}px;border:1px dashed #d0d0d0;border-radius:3px;color:#bbb;font-size:11px;line-height:${side}px;text-align:center;">${s.codeType === 'qr' ? '二维码' : '条形码'}</div></div>`;
    }
    return `<div style="text-align:${align};color:#999;font-size:12px;margin:8px 0;">(二维码/条码：内容为空)</div>`;
  }
  // 取数阶段编码失败时塞进来的是一句人话（见 barcode.ts 的 CODE128_UNSUPPORTED），
  // 印出来告诉用户该怎么办，而不是甩一张缺字符的条码或一句「内容为空」
  if (!src.startsWith('data:')) {
    return `<div style="text-align:${align};color:#c26442;font-size:12px;margin:8px 0;">(${esc(src)})</div>`;
  }
  const w = s.codeType === 'qr' ? `width:${Math.max(80, s.sizePx || 120)}px;` : `height:${Math.max(30, s.sizePx || 60)}px;`;
  return `<div style="text-align:${align};margin:8px 0;"><img src="${esc(src)}" alt="code" style="${w}max-width:100%;" /></div>`;
}

function renderRichText(s: RichTextSection, data: ContractRenderData): string {
  if (!s.fieldId) return '<div style="color:#999;font-size:12px;">(文章区块：请选择内容字段)</div>';
  const matched = findFieldByRef(data.mainFieldMetas, s.fieldId);
  const raw = matched ? data.mainRecord[matched.id] : data.mainRecord[s.fieldId];
  // 富文本源应为纯文本字段的原始字符串
  let value = '';
  if (typeof raw === 'string') value = raw;
  else if (Array.isArray(raw)) value = raw.map((x) => (x && typeof x === 'object' && 'text' in x ? (x as { text: string }).text : String(x))).join('');
  else if (raw != null) value = String(raw);
  if (!value && data.sampleMode) {
    return `<div style="margin:6px 0;">${samplePlaceholder(`文章区块 · ${s.format === 'markdown' ? 'Markdown' : 'HTML'}`)}</div>`;
  }
  return `<div style="margin:6px 0;">${renderRichTextValue(value, s.format)}</div>`;
}

function renderDivider(s: DividerSection): string {
  switch (s.style) {
    case 'line':
      return '<hr style="border:none;border-top:1px solid #ccc;margin:12px 0;" />';
    case 'page_break':
      return '<div style="page-break-after:always;"></div>';
    case 'blank':
      return '<div style="height:24px;"></div>';
  }
}

function renderSection(s: ContractSection, data: ContractRenderData, mode: RenderMode): string {
  switch (s.type) {
    case 'header': return renderHeader(s, data);
    case 'info_grid': return renderInfoGrid(s, data);
    case 'detail_table': return renderDetailTable(s, data);
    case 'free_table': return renderFreeTable(s, data, mode);
    case 'text_block': return renderTextBlock(s, data);
    case 'signature': return renderSignature(s, data);
    case 'attachment': return renderAttachment(s, data);
    case 'code': return renderCode(s, data);
    case 'rich_text': return renderRichText(s, data);
    case 'divider': return renderDivider(s);
    // 分栏要递归套编辑壳，在 buildContractHtml 的 renderList 里处理；这里只兜底
    case 'columns': return '';
  }
}

/**
 * 这段 HTML 里有没有实际内容。空的标题/文本块渲染出来只是一个空 div，
 * 在画布上看不见 —— 编辑态要据此换成明确的占位框。
 */
function isBlankBody(html: string): boolean {
  if (/<(img|table|svg|input|hr)\b/i.test(html)) return false;
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;|\s/g, '') === '';
}

/**
 * 斜杠菜单里能插的块。类型和名字都取自 types.ts 里那一份（左侧组件栏用的
 * 也是它）—— 渲染器这边曾经自己抄过一份标签，结果画布上叫「自由表格」、
 * 组件栏叫「表格」，同一个东西两个名字。别再抄第二份。「分栏」不是新块类型，而是一张无框线的一行多列表格 ——
 * 版面上就是这么回事，复用表格能省掉一整套嵌套结构。
 */
const SLASH_BLOCKS: Array<{ type: string; label: string }> = [
  ...INSERTABLE_SECTION_TYPES.map((t) => ({ type: t, label: SECTION_TYPE_LABELS[t] })),
  { type: 'columns', label: '分栏（两列并排）' },
];

/** 渲染模式：output=打印/导出/签署成品；preview=侧边栏预览；edit=可拖拽调位。 */
export type RenderMode = 'output' | 'preview' | 'edit';

/**
 * 平铺水印的 CSS：把文字画进一张 SVG，用 background-repeat 铺满整份文档，
 * 页数再多也不会漏。文字先做 XML 转义，再整体 URL 编码塞进 data URI。
 */
function tiledWatermarkCss(text: string): string {
  const tile = 260;
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${tile}" height="${tile}">` +
    `<text x="50%" y="50%" fill="rgba(0,0,0,0.06)" font-size="20" font-family="sans-serif" ` +
    `text-anchor="middle" dominant-baseline="middle" ` +
    `transform="rotate(-30 ${tile / 2} ${tile / 2})">${esc(text)}</text></svg>`;
  return `background-image:url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}");background-repeat:repeat;`;
}

/**
 * 三种浮层位置：
 *   page        整页的绝对层，top = 距页顶
 *   content_end 正文流末尾一个高度 0 的相对层，top = 距内容结束处
 *   section     挂靠区块：在那个顶层区块**前面**插一个高度 0 的相对层，
 *               top = 距该区块顶边 —— 上面的明细再长再短，章都跟着这个区块走
 * 挂靠的区块被删了，退到内容末尾，别让章凭空消失。
 */
function renderOverlays(
  template: ContractTemplate,
  mode: RenderMode,
): { page: string; end: string; bySection: Record<string, string> } {
  const items = template.overlays || [];
  const bySection: Record<string, string> = {};
  if (items.length === 0) return { page: '', end: '', bySection };
  const topIds = new Set(template.sections.map((s) => s.id));
  const render = (o: (typeof items)[number], topMm = o.yMm) => {
    const box = `position:absolute;left:${o.xMm}mm;top:${topMm}mm;width:${o.wMm}mm;height:${o.hMm}mm;`;
    if (o.kind === 'seal') {
      return `<div class="pp-ov" data-ov-id="${esc(o.id)}" data-ov-kind="seal" style="${box}">
<img src="${esc(o.dataUrl)}" alt="印章" style="width:100%;height:100%;object-fit:contain;mix-blend-mode:multiply;opacity:0.92;display:block;" draggable="false" />
</div>`;
    }
    // sign_area：成品输出时不画框（留白等待签名）；预览/编辑显示虚线占位。
    // data-ov-kind/label 供发起签名时实测像素坐标。
    const visible = mode !== 'output';
    const style = visible
      ? 'border:1.5px dashed #c26442;border-radius:2px;background:rgba(194,100,66,0.04);display:flex;align-items:center;justify-content:center;'
      : '';
    const tag = visible
      ? `<span style="font-size:10px;color:#c26442;background:#fff;padding:0 4px;">✍ ${esc(o.label)}</span>`
      : '';
    return `<div class="pp-ov" data-ov-id="${esc(o.id)}" data-ov-kind="sign_area" data-ov-label="${esc(o.label)}" style="${box}${style}">${tag}</div>`;
  };
  const pageDivs: string[] = [];
  const endDivs: string[] = [];
  for (const o of items) {
    if (o.anchor === 'section' && o.anchorSectionId && topIds.has(o.anchorSectionId)) {
      bySection[o.anchorSectionId] = (bySection[o.anchorSectionId] || '') + render(o);
    } else if (o.anchor === 'section') {
      endDivs.push(render(o, -o.hMm));
    } else if (o.anchor === 'content_end') {
      endDivs.push(render(o));
    } else {
      pageDivs.push(render(o));
    }
  }
  // z-index 必须高过正文流（它是 z-index:2）：否则印章 / 签名区在画布上被区块盖住，
  // 点下去选中的是底下的区块，拖不动；打印时章也会被表格压在下面
  return {
    page: pageDivs.length
      ? `<div class="pp-ov-layer" style="position:absolute;inset:0;z-index:3;pointer-events:none;">${pageDivs.join('\n')}</div>`
      : '',
    end: endDivs.length
      ? `<div class="pp-ov-layer pp-ov-layer-end" style="position:relative;height:0;z-index:3;pointer-events:none;">${endDivs.join('\n')}</div>`
      : '',
    bySection,
  };
}

/** 区块前面的挂靠层。编辑态每个顶层区块都放一个（拖拽时要量它们的位置），成品只在有章时放。 */
function sectionAnchorLayer(secId: string, inner: string | undefined, mode: RenderMode): string {
  if (!inner && mode !== 'edit') return '';
  return `<div class="pp-ov-layer pp-ov-anchor" data-anchor-sec="${esc(secId)}" style="position:relative;height:0;z-index:3;pointer-events:none;">${inner || ''}</div>`;
}

/** 预览态：真实纸张宽度按容器自适应缩放（CSS zoom）。 */
const ZOOM_FIT_SCRIPT = `<script>(function(){
  function fit(){
    document.body.style.zoom = 1;
    var z = Math.min(1, document.documentElement.clientWidth / document.body.offsetWidth);
    document.body.style.zoom = z;
    document.body.dataset.zoom = z;
    report();
  }
  // 把内容高度告诉宿主，iframe 跟着长：整张纸完整摊开，不在 iframe 里再套一层滚动
  var lastH = 0;
  function report(){
    var z = parseFloat(document.body.dataset.zoom || '1') || 1;
    var h = Math.ceil(document.body.offsetHeight * z);
    if (h && h !== lastH) { lastH = h; parent.postMessage({ type: 'pp-size', h: h }, '*'); }
  }
  fit();
  window.addEventListener('resize', fit);
  if (window.ResizeObserver) new ResizeObserver(report).observe(document.body);
  window.addEventListener('load', report);
})();</script>`;

/** 区块点选：hover 虚线 / 选中实线 + 浮动工具条（上移/下移/删除），
 *  与宿主面板双向联动（pp-section-click / pp-select / pp-section-move / -delete）。 */
const SECTION_EDIT_SCRIPT = `<style>
.pp-sec { position: relative; cursor: pointer; }
.pp-sec:hover { outline: 1.5px dashed rgba(194,100,66,0.5); outline-offset: 2px; }
.pp-sec.pp-sec-sel { outline: 2px solid #c26442; outline-offset: 2px; }
.pp-sec-bar { position: absolute; top: -14px; right: 0; display: none; gap: 2px; z-index: 5; }
.pp-sec.pp-sec-sel .pp-sec-bar { display: flex; }
.pp-sec-bar button { border: none; background: #c26442; color: #fff; font-size: 11px; line-height: 1;
  padding: 4px 7px; border-radius: 3px; cursor: pointer; }
.pp-sec-bar button:hover { background: #a54f31; }
.pp-ins { position: relative; height: 10px; margin: -5px 0; opacity: 0; transition: opacity .12s; }
.pp-ins:hover { opacity: 1; }
.pp-ins::before { content: ''; position: absolute; left: 0; right: 0; top: 5px; border-top: 1px dashed #c26442; }
.pp-ins button { position: absolute; left: 50%; top: 0; transform: translateX(-50%);
  border: none; background: #c26442; color: #fff; font-size: 11px; line-height: 1;
  padding: 2px 8px; border-radius: 8px; cursor: pointer; }
.pp-grip { position: absolute; left: -18px; top: 2px; width: 16px; height: 18px; display: none;
  align-items: center; justify-content: center; cursor: grab; color: #b9b3ab; font-size: 12px;
  user-select: none; z-index: 5; }
.pp-sec:hover .pp-grip, .pp-sec.pp-sec-sel .pp-grip { display: flex; }
.pp-sec.pp-dragging { opacity: .45; }
.pp-drop { border-top: 2px solid #c26442 !important; }
td[data-cell] { cursor: cell; }
td.pp-cell-sel { outline: 2px solid #c26442; outline-offset: -2px; background: rgba(194,100,66,0.08) !important; }
.pp-menu { position: fixed; z-index: 99; min-width: 168px; padding: 4px;
  background: #fff; border: 1px solid #e4ded6; border-radius: 8px;
  box-shadow: 0 6px 24px rgba(0,0,0,.14); font-size: 12px; color: #2f2b28; }
.pp-menu button { display: block; width: 100%; text-align: left; border: none; background: none;
  padding: 6px 10px; border-radius: 5px; cursor: pointer; font-size: 12px; color: inherit; }
.pp-menu button:hover:not(:disabled) { background: #f6f2ec; }
.pp-menu button:disabled { color: #bdb6ad; cursor: default; }
.pp-menu .pp-menu-sep { height: 1px; margin: 4px 2px; background: #eee8e0; }
.pp-menu .pp-menu-tip { padding: 4px 10px; color: #a49c92; font-size: 11px; }
.pp-menu button.pp-menu-active { background: #f6f2ec; }
.pp-colgrip { position: absolute; top: 0; bottom: 0; right: -4px; width: 8px; cursor: col-resize; z-index: 3; }
.pp-rowgrip { position: absolute; left: 0; right: 0; bottom: -4px; height: 8px; cursor: row-resize; z-index: 3; }
.pp-colgrip:hover, .pp-rowgrip:hover { background: rgba(194,100,66,0.35); }
.pp-flash { animation: ppflash 1.2s ease-out; }
.pp-tail { min-height: 56px; margin-top: 6px; padding: 8px 10px; border-radius: 6px; outline: none; white-space: pre-wrap; cursor: text; }
.pp-tail:empty::before { content: attr(data-ph); color: #b8ae9f; font-size: 12px; }
.pp-tail:focus { background: rgba(194,100,66,0.04); box-shadow: inset 0 0 0 1px rgba(194,100,66,0.25); }
.pp-ins-empty { min-height: 28px; display: flex; align-items: center; justify-content: center; border: 1px dashed #d8cfc4; border-radius: 6px; }
.pp-ins-empty button { display: inline-block !important; }
@keyframes ppflash { 0% { box-shadow: 0 0 0 5px rgba(194,100,66,.75); } 100% { box-shadow: 0 0 0 0 rgba(194,100,66,0); } }
.pp-bubble { position: fixed; z-index: 100; display: flex; align-items: center; gap: 2px; padding: 3px 4px;
  background: #2a2519; border-radius: 7px; box-shadow: 0 6px 20px rgba(0,0,0,.25); }
.pp-bubble button { border: none; background: none; color: #f7f3eb; font-size: 12px; line-height: 1; min-width: 24px;
  height: 24px; padding: 0 5px; border-radius: 4px; cursor: pointer; font-family: inherit; }
.pp-bubble button:hover { background: rgba(255,255,255,.14); }
.pp-bubble .pp-sw { width: 14px; height: 14px; border-radius: 50%; border: 1.5px solid rgba(255,255,255,.6); display: inline-block; }
.pp-bubble .pp-bsep { width: 1px; height: 16px; background: rgba(255,255,255,.2); margin: 0 3px; }
</style><script>(function(){
  var sel = null;
  function bar(el){
    var b = document.createElement('div');
    b.className = 'pp-sec-bar';
    b.innerHTML = '<button data-act="up" title="上移">↑</button><button data-act="down" title="下移">↓</button>' +
      '<button data-act="dup" title="复制区块">⧉</button><button data-act="del" title="删除">✕</button>';
    b.addEventListener('pointerdown', function(e){ e.stopPropagation(); });
    b.addEventListener('click', function(e){
      var act = e.target && e.target.dataset && e.target.dataset.act;
      if (!act) return;
      e.stopPropagation();
      var id = el.dataset.secId;
      if (act === 'del') parent.postMessage({ type: 'pp-section-delete', id: id }, '*');
      else if (act === 'dup') parent.postMessage({ type: 'pp-section-duplicate', id: id }, '*');
      else parent.postMessage({ type: 'pp-section-move', id: id, dir: act === 'up' ? -1 : 1 }, '*');
    });
    el.appendChild(b);
  }
  function select(el, silent){
    if (sel === el) return;
    if (sel) sel.classList.remove('pp-sec-sel');
    sel = el;
    if (el) {
      el.classList.add('pp-sec-sel');
      if (!el.querySelector('.pp-sec-bar')) bar(el);
      if (!silent) parent.postMessage({ type: 'pp-section-click', id: el.dataset.secId }, '*');
    }
  }
  Array.prototype.forEach.call(document.querySelectorAll('.pp-sec'), function(el){
    el.addEventListener('click', function(e){
      if (e.target.closest('.pp-ov')) return;
      e.stopPropagation(); // 分栏里的区块：别让外层分栏把选中抢走
      select(el, false);
      // 点到格子以外的地方：格子选区作废，右侧的「单元格」面板也该收起来
      if (document.querySelector('td.pp-cell-sel')) {
        clearCellSel();
        parent.postMessage({ type: 'pp-cell-select', id: el.dataset.secId, clear: true }, '*');
      }
    });
    // 右键区块：上下移、复制、删除、更换类型。表格格子有自己的右键菜单，
    // 那边 stopPropagation 了，不会走到这里。
    el.addEventListener('contextmenu', function(e){
      if (e.target.closest('.pp-ov')) return;
      e.preventDefault();
      e.stopPropagation();
      select(el, false);
      var id = el.dataset.secId;
      var items = [
        { tip: '区块' },
        { label: '上移', run: function(){ parent.postMessage({ type: 'pp-section-move', id: id, dir: -1 }, '*'); } },
        { label: '下移', run: function(){ parent.postMessage({ type: 'pp-section-move', id: id, dir: 1 }, '*'); } },
        { label: '复制区块', run: function(){ parent.postMessage({ type: 'pp-section-duplicate', id: id }, '*'); } },
        { label: '删除区块', run: function(){ parent.postMessage({ type: 'pp-section-delete', id: id }, '*'); } },
        { sep: true },
        { tip: '更换为' },
      ].concat(BLOCKS.map(function(b){
        return { label: b.label, run: function(){
          parent.postMessage({ type: 'pp-section-convert', id: id, blockType: b.type }, '*');
        } };
      }));
      openMenu(e.clientX, e.clientY, items);
    });
  });
  // ── 通用小菜单：右键表格菜单和斜杠插块菜单共用它，避免做出两套 ──
  var menuEl = null;
  function closeMenu(){
    if (menuEl) { menuEl.remove(); menuEl = null; }
    if (menuKeys) { document.removeEventListener('keydown', menuKeys, true); menuKeys = null; }
    if (menuInput && menuInputEl) { menuInputEl.removeEventListener('input', menuInput); }
    menuInput = null; menuInputEl = null;
  }
  document.addEventListener('click', function(e){
    if (menuEl && !menuEl.contains(e.target)) closeMenu();
  });
  document.addEventListener('keydown', function(e){
    if (e.key === 'Escape') closeMenu();
    // 撤销/重做：iframe 里按的快捷键宿主收不到，转发出去。正在就地编辑时
    // 交给编辑框自己的撤销（那是浏览器原生的，按字符退）。
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
      var t = e.target;
      if (t && t.isContentEditable) return;
      e.preventDefault();
      parent.postMessage({ type: e.shiftKey ? 'pp-redo' : 'pp-undo' }, '*');
    }
  });
  // opts.typeahead: 像飞书文档那样 —— 菜单开着时继续打字筛选，↑↓ 选，Enter 确认。
  // 筛选词不从按键读（中文输入法走 composition，keydown 里只有 'Process'），
  // 而是监听正文的 input 事件、取最后一个 / 后面的字。字照常留在正文里，
  // 选中项后由调用方连同 / 一起抹掉。
  var menuKeys = null, menuInput = null, menuInputEl = null;
  function openMenu(x, y, items, opts){
    closeMenu();
    var m = document.createElement('div');
    m.className = 'pp-menu';
    var filter = '';
    var active = -1;
    var tipEl = null;
    var buttons = [];
    function render(){
      buttons.forEach(function(b){
        var hit = !filter || b.textContent.toLowerCase().indexOf(filter.toLowerCase()) >= 0;
        b.style.display = hit ? '' : 'none';
      });
      var visible = buttons.filter(function(b){ return b.style.display !== 'none' && !b.disabled; });
      if (active >= visible.length) active = visible.length - 1;
      if (active < 0 && visible.length && opts && opts.typeahead) active = 0;
      buttons.forEach(function(b){ b.classList.remove('pp-menu-active'); });
      if (active >= 0 && visible[active]) visible[active].classList.add('pp-menu-active');
      if (tipEl) tipEl.textContent = (opts && opts.tip || '') + (filter ? ' · 筛选：' + filter : '');
      return visible;
    }
    items.forEach(function(it){
      if (it.sep) { var d = document.createElement('div'); d.className = 'pp-menu-sep'; m.appendChild(d); return; }
      if (it.tip) { var t = document.createElement('div'); t.className = 'pp-menu-tip'; t.textContent = it.tip; if (!tipEl) tipEl = t; m.appendChild(t); return; }
      var b = document.createElement('button');
      b.textContent = it.label;
      if (it.disabled) b.disabled = true;
      else b.addEventListener('click', function(ev){ ev.stopPropagation(); closeMenu(); it.run(filter); });
      b._run = it.run;
      buttons.push(b);
      m.appendChild(b);
    });
    document.body.appendChild(m);
    // 贴边时往回收，别把菜单顶出可视区
    var r = m.getBoundingClientRect();
    m.style.left = Math.min(x, window.innerWidth - r.width - 8) + 'px';
    m.style.top = Math.min(y, window.innerHeight - r.height - 8) + 'px';
    menuEl = m;
    if (opts && opts.typeahead) {
      render();
      menuKeys = function(e){
        if (e.isComposing) return;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          var vis = render();
          if (!vis.length) return;
          active = (active + (e.key === 'ArrowDown' ? 1 : -1) + vis.length) % vis.length;
          render();
        } else if (e.key === 'Enter') {
          e.preventDefault();
          var v2 = render();
          var b = v2[active];
          if (b) { var f = filter; closeMenu(); b._run(f); }
        }
      };
      document.addEventListener('keydown', menuKeys, true);
      menuInputEl = opts.typeahead.source || null;
      if (menuInputEl) {
        menuInput = function(){
          var t = menuInputEl.textContent || '';
          var i = t.lastIndexOf('/');
          if (i === -1) { closeMenu(); return; } // 把 / 删掉了：菜单跟着收
          filter = t.slice(i + 1);
          render();
        };
        menuInputEl.addEventListener('input', menuInput);
      }
    }
  }

  // ── 表格：画布上直接选格子、右键做结构操作 ──
  //
  // 以前合并单元格得先点开一个弹窗网格编辑器，在另一张示意表上操作，
  // 再回来看效果。现在就在成品上选、就在成品上改。
  var cellAnchor = null; // {sec,r,c} 最近一次单击的格子，Shift 以它为起点拉矩形
  function cellsOf(sec){
    return Array.prototype.slice.call(document.querySelectorAll('td[data-cell][data-sec="' + sec + '"]'));
  }
  function clearCellSel(){
    Array.prototype.forEach.call(document.querySelectorAll('td.pp-cell-sel'), function(td){
      td.classList.remove('pp-cell-sel');
    });
  }
  function markCellSel(sec, r0, c0, r1, c1){
    clearCellSel();
    cellsOf(sec).forEach(function(td){
      var r = +td.dataset.r, c = +td.dataset.c;
      if (r >= Math.min(r0, r1) && r <= Math.max(r0, r1) && c >= Math.min(c0, c1) && c <= Math.max(c0, c1)) {
        td.classList.add('pp-cell-sel');
      }
    });
    // 宿主据此显示「单元格」样式面板、把左栏点的字段落进格子
    parent.postMessage({ type: 'pp-cell-select', id: sec,
      r0: Math.min(r0, r1), c0: Math.min(c0, c1), r1: Math.max(r0, r1), c1: Math.max(c0, c1) }, '*');
  }
  function selRect(){
    var sel = document.querySelectorAll('td.pp-cell-sel');
    if (!sel.length) return null;
    var sec = sel[0].dataset.sec, r0 = 1e9, c0 = 1e9, r1 = -1, c1 = -1;
    Array.prototype.forEach.call(sel, function(td){
      var r = +td.dataset.r, c = +td.dataset.c;
      r0 = Math.min(r0, r); c0 = Math.min(c0, c);
      r1 = Math.max(r1, r); c1 = Math.max(c1, c);
    });
    return { sec: sec, r0: r0, c0: c0, r1: r1, c1: c1 };
  }
  function tableOp(op, extra){
    var rect = selRect();
    if (!rect) return;
    var msg = { type: 'pp-table-op', op: op, id: rect.sec, r0: rect.r0, c0: rect.c0, r1: rect.r1, c1: rect.c1 };
    if (extra) for (var k in extra) msg[k] = extra[k];
    parent.postMessage(msg, '*');
  }

  Array.prototype.forEach.call(document.querySelectorAll('td[data-cell]'), function(td){
    td.addEventListener('click', function(e){
      e.stopPropagation();
      // 点格子也算点中了这张表：右侧属性栏要切到它
      var host = td.closest('.pp-sec');
      if (host) select(host, false);
      var sec = td.dataset.sec, r = +td.dataset.r, c = +td.dataset.c;
      if (e.shiftKey && cellAnchor && cellAnchor.sec === sec) {
        markCellSel(sec, cellAnchor.r, cellAnchor.c, r, c);
      } else {
        cellAnchor = { sec: sec, r: r, c: c };
        markCellSel(sec, r, c, r, c);
      }
    });
    td.addEventListener('contextmenu', function(e){
      e.preventDefault();
      e.stopPropagation();
      var sec = td.dataset.sec, r = +td.dataset.r, c = +td.dataset.c;
      // 右键的格子不在选区里就先单选它，符合表格软件的一贯行为
      if (!td.classList.contains('pp-cell-sel')) {
        cellAnchor = { sec: sec, r: r, c: c };
        markCellSel(sec, r, c, r, c);
      }
      var rect = selRect();
      var multi = rect && (rect.r1 > rect.r0 || rect.c1 > rect.c0);
      var spanned = td.colSpan > 1 || td.rowSpan > 1;
      openMenu(e.clientX, e.clientY, [
        { tip: '单元格' },
        { label: '合并单元格', disabled: !multi, run: function(){ tableOp('merge'); } },
        { label: '拆分单元格', disabled: !spanned, run: function(){ tableOp('split'); } },
        { sep: true },
        { tip: '行' },
        { label: '在上方插入行', run: function(){ tableOp('insertRow', { at: rect.r0 }); } },
        { label: '在下方插入行', run: function(){ tableOp('insertRow', { at: rect.r1 + 1 }); } },
        { label: '删除本行', run: function(){ tableOp('removeRow', { at: rect.r0 }); } },
        { sep: true },
        { tip: '列' },
        { label: '在左侧插入列', run: function(){ tableOp('insertCol', { at: rect.c0 }); } },
        { label: '在右侧插入列', run: function(){ tableOp('insertCol', { at: rect.c1 + 1 }); } },
        { label: '删除本列', run: function(){ tableOp('removeCol', { at: rect.c0 }); } },
        { sep: true },
        { label: '均分列宽', run: function(){ tableOp('evenCols'); } },
        { label: '重置行高（按内容自适应）', run: function(){ tableOp('rowHeight', { at: rect.r0, value: null }); } },
        { label: '本行行高应用到全表', run: function(){ tableOp('rowHeightAll', { at: rect.r0 }); } },
      ]);
    });
  });

  // ── 拖列线 / 行线调宽高，像 Excel ──
  //
  // 列线只动相邻两列（左宽右窄，总宽不变），拖完才提交；拖的过程直接改
  // colgroup 里的宽度做实时反馈，不经过宿主重渲染 —— 那一来一回有几百毫秒，
  // 跟手感是这个功能的全部。
  function zoomOf(){ return parseFloat(document.body.dataset.zoom || '1') || 1; }
  var PX_PER_MM = 96 / 25.4;
  Array.prototype.forEach.call(document.querySelectorAll('table'), function(table){
    // 只认直接属于这张表的格子：外层的骨架表也能 querySelectorAll 到里面的 td，
    // 不筛的话每个把手都会被装两遍
    var tds = Array.prototype.filter.call(table.querySelectorAll('td[data-cell]'), function(td){
      return td.closest('table') === table;
    });
    if (!tds.length) return;
    var cols = table.querySelectorAll('colgroup col');
    var secId = tds[0].dataset.sec;
    var firstRow = tds[0].parentNode;
    Array.prototype.forEach.call(firstRow.querySelectorAll('td[data-cell]'), function(td){
      var c = +td.dataset.c + (td.colSpan || 1) - 1;
      if (c >= cols.length - 1) return; // 最后一列右边没有邻居
      td.style.position = 'relative';
      var h = document.createElement('div');
      h.className = 'pp-colgrip'; h.title = '拖动调整列宽';
      td.appendChild(h);
      h.addEventListener('pointerdown', function(e){
        e.preventDefault(); e.stopPropagation();
        var z = zoomOf(), tw = table.offsetWidth || 1;
        var w0 = parseFloat(cols[c].style.width) || 0, w1 = parseFloat(cols[c + 1].style.width) || 0;
        var sx = e.clientX, cur = w0;
        function mv(ev){
          var d = (ev.clientX - sx) / z / tw * 100;
          cur = Math.max(3, Math.min(w0 + w1 - 3, w0 + d));
          cols[c].style.width = cur + '%';
          cols[c + 1].style.width = (w0 + w1 - cur) + '%';
        }
        function up(){
          document.removeEventListener('pointermove', mv);
          document.removeEventListener('pointerup', up);
          parent.postMessage({ type: 'pp-table-op', op: 'colResize', id: secId,
            r0: 0, c0: c, r1: 0, c1: c, at: c, value: Math.round(cur * 10) / 10 }, '*');
        }
        document.addEventListener('pointermove', mv);
        document.addEventListener('pointerup', up);
      });
    });
    var seen = {};
    Array.prototype.forEach.call(tds, function(td){
      var r = +td.dataset.r;
      if (seen[r] || (td.rowSpan || 1) > 1) return; // 跨行格的底边不是这一行的底边
      seen[r] = 1;
      var tr = td.parentNode;
      td.style.position = 'relative';
      var h = document.createElement('div');
      h.className = 'pp-rowgrip'; h.title = '拖动调整行高';
      td.appendChild(h);
      h.addEventListener('pointerdown', function(e){
        e.preventDefault(); e.stopPropagation();
        var z = zoomOf(), h0 = tr.offsetHeight, sy = e.clientY, cur = h0;
        function mv(ev){ cur = Math.max(12, h0 + (ev.clientY - sy) / z); tr.style.height = cur + 'px'; }
        function up(){
          document.removeEventListener('pointermove', mv);
          document.removeEventListener('pointerup', up);
          parent.postMessage({ type: 'pp-table-op', op: 'rowHeight', id: secId,
            r0: r, c0: 0, r1: r, c1: 0, at: r, value: Math.round(cur / PX_PER_MM * 10) / 10 }, '*');
        }
        document.addEventListener('pointermove', mv);
        document.addEventListener('pointerup', up);
      });
    });
  });

  // ── 就地编辑：双击标题/文章区块直接改字，不用去下面的面板里找 ──
  /**
   * 就地编辑。el = 携带 data-raw 的元素（区块或表格格子），
   * body = 真正放文字的节点，onCommit 收纯文本。
   */
  // 编辑框里的 HTML → 我们认的那一小撮标记。浏览器在 contenteditable 里产出的
  // 东西五花八门（div/font/带一堆 style 的 span），这里只留 b/i/u 和
  // 颜色/字号/背景，其他统统拍平成文字。换行统一成换行符（区块是 pre-wrap 的）。
  var FONT_PX = { 1: 10, 2: 12, 3: 14, 4: 16, 5: 18, 6: 22, 7: 28 };
  function escText(t){ return t.replace(/\\u00a0/g, ' ').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
  function cleanHtml(html, host){
    var d = document.createElement('div'); d.innerHTML = html;
    // 容器自己的字号 / 粗细：span 上和它一样的值是浏览器补的，不是用户在气泡里改的
    var base = host ? getComputedStyle(host) : null;
    var basePx = base ? Math.round(parseFloat(base.fontSize)) : 0;
    var baseBold = base ? (base.fontWeight === 'bold' || +base.fontWeight >= 600) : false;
    function walk(node){
      var out = '';
      Array.prototype.forEach.call(node.childNodes, function(n){
        if (n.nodeType === 3) { out += escText(n.nodeValue); return; }
        if (n.nodeType !== 1) return;
        var tag = n.tagName.toLowerCase();
        if (tag === 'br') { out += '\\n'; return; }
        var inner = walk(n);
        if (tag === 'div' || tag === 'p') { out += (out && !/\\n$/.test(out) ? '\\n' : '') + inner; return; }
        if (tag === 'b' || tag === 'strong') { out += '<b>' + inner + '</b>'; return; }
        if (tag === 'i' || tag === 'em') { out += '<i>' + inner + '</i>'; return; }
        if (tag === 'u') { out += '<u>' + inner + '</u>'; return; }
        var st = '';
        if (tag === 'font') {
          if (n.color) st += 'color:' + n.color + ';';
          if (n.size && FONT_PX[n.size]) st += 'font-size:' + FONT_PX[n.size] + 'px;';
        } else if (tag === 'span') {
          var cs = n.style;
          if (cs.color) st += 'color:' + cs.color + ';';
          if (cs.fontSize && Math.round(parseFloat(cs.fontSize)) !== basePx) st += 'font-size:' + cs.fontSize + ';';
          var spanBold = cs.fontWeight === 'bold' || +cs.fontWeight >= 600;
          var spanNormal = cs.fontWeight === 'normal' || +cs.fontWeight === 400;
          if (spanBold && !baseBold) st += 'font-weight:700;';
          // 粗体标题里取消一段加粗：normal 也得留住，否则点了没反应
          else if (spanNormal && baseBold) st += 'font-weight:400;';
          if (cs.fontStyle === 'italic') st += 'font-style:italic;';
          if ((cs.textDecoration || '').indexOf('underline') >= 0) st += 'text-decoration:underline;';
          if (cs.backgroundColor) st += 'background-color:' + cs.backgroundColor + ';';
        }
        out += st ? '<span style="' + st + '">' + inner + '</span>' : inner;
      });
      return out;
    }
    return walk(d);
  }

  // 选中一段文字弹出来的小工具条：加粗/斜体/下划线/颜色/字号
  var bubbleEl = null;
  function closeBubble(){ if (bubbleEl) { bubbleEl.remove(); bubbleEl = null; } }
  function cmd(name, val){
    try { document.execCommand('styleWithCSS', false, true); } catch (err) {}
    document.execCommand(name, false, val);
  }
  function bumpFontSize(delta){
    var sl = window.getSelection();
    if (!sl.rangeCount) return;
    var node = sl.anchorNode;
    var elx = node && node.nodeType === 3 ? node.parentNode : node;
    var cur = parseFloat(getComputedStyle(elx).fontSize) || 12;
    var next = Math.max(8, Math.min(48, Math.round(cur + delta)));
    // execCommand 只认 1-7 的档位；先塞个 7 再把它产出的 <font> 改成精确像素
    cmd('fontSize', '7');
    Array.prototype.forEach.call(document.querySelectorAll('font[size="7"]'), function(f){
      var sp = document.createElement('span');
      sp.style.fontSize = next + 'px';
      while (f.firstChild) sp.appendChild(f.firstChild);
      f.parentNode.replaceChild(sp, f);
    });
  }
  function showBubble(body){
    var sl = window.getSelection();
    if (!sl.rangeCount || sl.isCollapsed || !body.contains(sl.anchorNode)) { closeBubble(); return; }
    var r = sl.getRangeAt(0).getBoundingClientRect();
    if (!bubbleEl) {
      var bb = document.createElement('div');
      bb.className = 'pp-bubble';
      var COLORS = ['#000000', '#c0392b', '#1f6feb', '#2d6a4f', '#7a5210', '#8e44ad'];
      bb.innerHTML =
        '<button data-c="bold" title="加粗"><b>B</b></button>' +
        '<button data-c="italic" title="斜体"><i>I</i></button>' +
        '<button data-c="underline" title="下划线"><u>U</u></button>' +
        '<span class="pp-bsep"></span>' +
        COLORS.map(function(c){ return '<button data-color="' + c + '" title="文字颜色"><span class="pp-sw" style="background:' + c + '"></span></button>'; }).join('') +
        '<span class="pp-bsep"></span>' +
        '<button data-size="-2" title="字号减小">A-</button><button data-size="2" title="字号加大">A+</button>' +
        '<span class="pp-bsep"></span>' +
        '<button data-c="removeFormat" title="清除样式">清除</button>';
      // 按下去不能让编辑框失焦、不能丢选区
      bb.addEventListener('mousedown', function(e){ e.preventDefault(); e.stopPropagation(); });
      bb.addEventListener('click', function(e){
        var b = e.target.closest('button'); if (!b) return;
        e.preventDefault(); e.stopPropagation();
        if (b.dataset.c) cmd(b.dataset.c);
        else if (b.dataset.color) cmd('foreColor', b.dataset.color);
        else if (b.dataset.size) bumpFontSize(+b.dataset.size);
        setTimeout(function(){ showBubble(body); }, 0);
      });
      document.body.appendChild(bb);
      bubbleEl = bb;
    }
    var w = bubbleEl.offsetWidth, hgt = bubbleEl.offsetHeight;
    bubbleEl.style.left = Math.max(4, Math.min(r.left + r.width / 2 - w / 2, window.innerWidth - w - 4)) + 'px';
    bubbleEl.style.top = Math.max(4, r.top - hgt - 8) + 'px';
  }

  /**
   * rich=true：标题/文本段落，内容允许一小撮 HTML（加粗、颜色…），编辑时所见
   * 即所得。rich=false：表格格子，纯文本 —— 格子里的标签会把 td 撑破。
   */
  function editIn(el, body, onCommit, rich){
    if (el.dataset.editing === '1') return;
    el.dataset.editing = '1';
    el.dataset.before = el.dataset.raw || '';
    if (rich) body.innerHTML = el.dataset.raw || '';
    else body.textContent = el.dataset.raw || '';
    body.setAttribute('contenteditable', rich ? 'true' : 'plaintext-only');
    body.style.outline = '2px solid #c26442';
    body.style.outlineOffset = '2px';
    body.style.whiteSpace = 'pre-wrap';
    body.focus();
    // 光标落在末尾，不要全选。全选看着方便，但下一个键就把整块内容冲掉了 ——
    // 格子里那句 {{客户名称}} 只想在后面补两个字的人，会直接失去这个字段引用。
    // 真要整段重写，编辑框里 Cmd+A 照样管用。
    try {
      var r = document.createRange(); r.selectNodeContents(body); r.collapse(false);
      var sl = window.getSelection(); sl.removeAllRanges(); sl.addRange(r);
    } catch (err) {}
    function commit(save){
      if (el.dataset.editing !== '1') return;
      el.dataset.editing = '';
      body.removeAttribute('contenteditable');
      body.style.outline = '';
      var text = rich ? cleanHtml(body.innerHTML, body) : (body.textContent || '');
      body.removeEventListener('blur', onBlur);
      body.removeEventListener('keydown', onKey);
      body.removeEventListener('mouseup', onSel);
      body.removeEventListener('keyup', onSel);
      body.removeEventListener('paste', onPaste);
      closeBubble();
      closeMenu();
      if (save && text !== el.dataset.before) onCommit(text);
      // 没改就把渲染值放回去（宿主重渲染前不至于一直显示占位符）
      else body.textContent = el.dataset.before || '';
    }
    // 点斜杠菜单会让编辑框失焦；菜单开着时先别提交
    function onBlur(){ setTimeout(function(){ if (!menuEl && document.activeElement !== body) commit(true); }, 120); }
    function onKey(e){
      if (e.key === 'Escape') { e.preventDefault(); commit(false); }
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); commit(true); }
      // 斜杠菜单：像飞书文档那样敲 / 唤起插块
      if (e.key === '/') setTimeout(function(){ slashMenu(el, body, commit); }, 0);
    }
    function onSel(){ if (rich) setTimeout(function(){ showBubble(body); }, 0); }
    // 粘贴只收纯文本：Word / 网页带来的每段 font-size 都和容器不一样会被留下，
    // 之后在属性栏改块字号对那些段落又不生效 —— 和整块 span 是同一类坑
    function onPaste(e){
      e.preventDefault();
      var t = (e.clipboardData || window.clipboardData).getData('text/plain') || '';
      document.execCommand('insertText', false, t);
    }
    body.addEventListener('paste', onPaste);
    body.addEventListener('blur', onBlur);
    body.addEventListener('keydown', onKey);
    body.addEventListener('mouseup', onSel);
    body.addEventListener('keyup', onSel);
  }

  // ── 斜杠菜单 ──────────────────────────────────────
  //
  // 在文字里敲 / 就能插块，不用把手移到左边的组件栏。菜单里的块类型由宿主
  // 通过 data-blocks 传进来，和面板里的列表同一份，不会两边对不上。
  var BLOCKS = [];
  try { BLOCKS = JSON.parse(document.body.dataset.blocks || '[]'); } catch (err) { BLOCKS = []; }
  function slashMenu(el, body, commit){
    if (!BLOCKS.length) return;
    var rect;
    try {
      var r = window.getSelection().getRangeAt(0).getBoundingClientRect();
      rect = (r.width || r.height) ? r : (body || el).getBoundingClientRect();
    } catch (err) { rect = (body || el).getBoundingClientRect(); }
    openMenu(rect.left, rect.bottom + 4, [{ tip: '插入块' }].concat(
      BLOCKS.map(function(b){
        return {
          label: b.label,
          run: function(){
            // 把刚敲的 / 连同后面用来筛选的字一起去掉再提交，否则正文里会留一截「/表」
            var strip = function(t){ return (t || '').replace(/[/][^/]*$/, ''); };
            if (!body) {
              // 没有编辑框（选中区块直接敲的 /）：不用清理正文
            } else if (body.isContentEditable && body.getAttribute('contenteditable') === 'true') {
              var last = body.lastChild;
              while (last && last.nodeType === 1 && last.lastChild) last = last.lastChild;
              if (last && last.nodeType === 3) last.nodeValue = strip(last.nodeValue);
              else body.innerHTML = strip(body.innerHTML);
            } else {
              body.textContent = strip(body.textContent);
            }
            if (commit) commit(true);
            parent.postMessage({
              type: 'pp-section-insert-after',
              id: el.dataset.secId || el.dataset.sec || '__end__',
              blockType: b.type,
            }, '*');
          },
        };
      }),
    ), { typeahead: { source: body || null }, tip: '插入块（继续打字筛选，↑↓ 选，Enter 确认）' });
  }

  // ── 胶囊点击改绑：点正文 / 格子 / 信息网格里的 {{字段}} 胶囊，换成别的字段 ──
  //
  // 以前改绑要双击进编辑态手改 {{}} 里的字，或去右侧属性栏翻下拉。
  var FIELDS = [];
  try { FIELDS = JSON.parse(document.body.dataset.fields || '[]'); } catch (err) { FIELDS = []; }
  Array.prototype.forEach.call(document.querySelectorAll('[data-chip]'), function(chip){
    chip.addEventListener('click', function(e){
      e.preventDefault(); e.stopPropagation();
      if (!FIELDS.length) return;
      var host = chip.closest('.pp-sec');
      if (host) select(host, false);
      var from = chip.dataset.chip;
      var td = chip.closest('td[data-cell]');
      var r = chip.getBoundingClientRect();
      openMenu(r.left, r.bottom + 4, [{ tip: '把「' + from.split('|')[0] + '」换成' }].concat(
        FIELDS.map(function(name){
          return { label: name, run: function(){
            parent.postMessage({ type: 'pp-rebind', id: host ? host.dataset.secId : '', from: from, to: name,
              r: td ? +td.dataset.r : null, c: td ? +td.dataset.c : null }, '*');
          } };
        })
      ), { typeahead: {}, tip: '把「' + from.split('|')[0] + '」换成（↑↓ 选，Enter 确认）' });
    });
  });

  // ── 末尾的空段落：画布最下面永远能点进去打字、敲 / 插块 ──
  //
  // 竞品的画布是文档流，末尾天然有个空段落；我们的区块是一个个挂上去的，
  // 末尾空白处以前什么都不是，用户点了敲 / 没反应。
  var tail = document.querySelector('.pp-tail');
  function lastTopSectionId(){
    var flow = document.querySelector('.pp-flow');
    var secs = flow ? flow.querySelectorAll(':scope > .pp-sec') : [];
    return secs.length ? secs[secs.length - 1].dataset.secId : '__end__';
  }
  if (tail) {
    tail.addEventListener('click', function(e){ e.stopPropagation(); });
    tail.addEventListener('keydown', function(e){
      if (e.key === '/') {
        tail.dataset.secId = lastTopSectionId();
        setTimeout(function(){ slashMenu(tail, tail, function(){ tail.textContent = ''; }); }, 0);
      } else if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        tail.blur();
      }
    });
    // 打了字离开：变成一个文本段落挂在末尾
    tail.addEventListener('blur', function(){
      setTimeout(function(){
        if (menuEl) return;
        var text = (tail.textContent || '').trim();
        // 敲了 / 又按 Esc 取消：那个 / 不是正文，别生成一个只有斜杠的段落
        if (!text || text === '/') { tail.textContent = ''; return; }
        tail.textContent = '';
        parent.postMessage({ type: 'pp-section-insert-after', id: lastTopSectionId(), blockType: 'text_block', text: text }, '*');
      }, 120);
    });
  }
  // 选中了某个区块、光标不在任何编辑框里：直接敲 / 在它后面插块
  document.addEventListener('keydown', function(e){
    if (e.key !== '/' || e.metaKey || e.ctrlKey) return;
    var t = e.target;
    if (t && t.isContentEditable) return;
    if (!sel) return;
    e.preventDefault();
    slashMenu(sel, null, null);
  });
  // ── 拖拽排序：抓住左侧六点手柄拖到目标位置 ──
  //
  // 以前只能点浮动条上的 ↑↓，把一个区块从末尾挪到开头要点很多次。
  // 用原生 drag 事件而不是指针位移：iframe 里 zoom 过，指针坐标要换算，
  // 而 dragover 只需要判断落在哪个区块的上/下半区，跟缩放无关。
  var dragId = null;
  function clearDrop(){
    Array.prototype.forEach.call(document.querySelectorAll('.pp-drop'), function(x){
      x.classList.remove('pp-drop');
    });
  }
  Array.prototype.forEach.call(document.querySelectorAll('.pp-sec'), function(el){
    var g = document.createElement('div');
    g.className = 'pp-grip';
    g.textContent = '⠿';
    g.title = '拖动调整顺序';
    g.draggable = true;
    g.addEventListener('dragstart', function(e){
      dragId = el.dataset.secId;
      el.classList.add('pp-dragging');
      try { e.dataTransfer.setData('text/plain', dragId); e.dataTransfer.effectAllowed = 'move'; } catch (err) {}
    });
    g.addEventListener('dragend', function(){
      el.classList.remove('pp-dragging');
      clearDrop();
      dragId = null;
    });
    el.appendChild(g);

    el.addEventListener('dragover', function(e){
      if (!dragId || el.dataset.secId === dragId) return;
      e.preventDefault();
      clearDrop();
      el.classList.add('pp-drop');
    });
    el.addEventListener('drop', function(e){
      if (!dragId || el.dataset.secId === dragId) return;
      e.preventDefault();
      var r = el.getBoundingClientRect();
      var before = (e.clientY - r.top) < r.height / 2;
      parent.postMessage({
        type: 'pp-section-reorder', id: dragId, targetId: el.dataset.secId, before: before,
      }, '*');
      clearDrop();
      dragId = null;
    });
  });

  // ── 区块之间的插入点：鼠标移到缝隙上出现「+」，点了在这个位置插块 ──
  Array.prototype.forEach.call(document.querySelectorAll('.pp-ins'), function(el){
    var b = document.createElement('button');
    b.textContent = el.classList.contains('pp-ins-empty') ? '+ 这一栏还是空的，点这里放内容' : '+ 在这里插入';
    b.addEventListener('click', function(e){
      e.stopPropagation();
      parent.postMessage({ type: 'pp-section-insert', index: Number(el.dataset.insAt),
        parentId: el.dataset.parent || null, col: el.dataset.col != null ? Number(el.dataset.col) : null }, '*');
    });
    el.appendChild(b);
  });

  Array.prototype.forEach.call(document.querySelectorAll('.pp-sec[data-edit="text"]'), function(el){
    el.style.cursor = 'text';
    el.title = '双击改字 · 敲 / 插入块';
    el.addEventListener('dblclick', function(e){
      e.stopPropagation();
      var body = el.querySelector(':scope > div:not(.pp-sec-bar)') || el;
      editIn(el, body, function(text){
        parent.postMessage({ type: 'pp-section-text', id: el.dataset.secId, text: text }, '*');
      }, true);
    });
  });

  // 表格格子同样能双击改字 —— 以前只能进弹窗网格编辑器改
  Array.prototype.forEach.call(document.querySelectorAll('td[data-cell]'), function(td){
    td.title = '单击选中 · 双击改字 · 右键行列与合并 · Shift 单击拉选区';
    td.addEventListener('dblclick', function(e){
      e.stopPropagation();
      editIn(td, td, function(text){
        parent.postMessage({
          type: 'pp-cell-text', id: td.dataset.sec,
          r: +td.dataset.r, c: +td.dataset.c, text: text,
        }, '*');
      }, false);
    });
  });

  window.addEventListener('message', function(e){
    var d = e.data;
    if (d && d.type === 'pp-select') {
      var el = document.querySelector('.pp-sec[data-sec-id="' + d.id + '"]');
      if (el) {
        select(el, true);
        el.scrollIntoView({ block: d.flash ? 'center' : 'nearest', behavior: 'smooth' });
        if (d.flash) {
          el.classList.remove('pp-flash'); void el.offsetWidth; el.classList.add('pp-flash');
        }
      }
    }
  });
})();</script>`;

/** 编辑态注入的拖拽/缩放脚本：把变更 postMessage 回宿主。
 *  页面按容器 zoom 缩放显示，指针位移要除以 zoom 换回布局像素。 */
const OVERLAY_EDIT_SCRIPT = ZOOM_FIT_SCRIPT + SECTION_EDIT_SCRIPT + `<script>(function(){
  var layers = document.querySelectorAll('.pp-ov-layer');
  if (!layers.length) return;
  Array.prototype.forEach.call(layers, function(l){ l.style.pointerEvents = 'none'; });
  var host = document.querySelector('.pp-ov-host');
  function pxPerMm(){ return host.clientWidth / parseFloat(host.dataset.contentWmm); }
  function zoom(){ return parseFloat(document.body.dataset.zoom || '1') || 1; }
  // offsetTop 相对各自的层：整页层 = 距页顶，末尾层 / 区块挂靠层（高度 0）= 距那个点，可为负
  Array.prototype.forEach.call(document.querySelectorAll('.pp-ov'), function(el){
    el.style.pointerEvents = 'auto';
    el.style.cursor = 'move';
    el.style.outline = '1px solid rgba(194,100,66,0.6)';
    el.style.touchAction = 'none';
    // 右下角缩放柄
    var h = document.createElement('div');
    h.style.cssText = 'position:absolute;right:-6px;bottom:-6px;width:12px;height:12px;background:#c26442;border-radius:2px;cursor:nwse-resize;';
    el.appendChild(h);
    var drag = null;
    function down(e, resize){
      e.preventDefault(); e.stopPropagation();
      drag = { resize: resize, sx: e.clientX, sy: e.clientY,
               x: el.offsetLeft, y: el.offsetTop, w: el.offsetWidth, hgt: el.offsetHeight };
    }
    el.addEventListener('pointerdown', function(e){ if (e.target === h) return; down(e, false); });
    h.addEventListener('pointerdown', function(e){ down(e, true); });
    document.addEventListener('pointermove', function(e){
      if (!drag) return;
      var z = zoom();
      var dx = (e.clientX - drag.sx) / z, dy = (e.clientY - drag.sy) / z;
      if (drag.resize) {
        el.style.width = Math.max(15, drag.w + dx) + 'px';
        el.style.height = Math.max(10, drag.hgt + dy) + 'px';
      } else {
        el.style.left = Math.max(0, drag.x + dx) + 'px';
        // 跟随内容的层（末尾 / 挂靠区块）高度为 0，往上压就是负数，不能夹到 0
        var rel = el.parentNode && el.parentNode !== host && el.parentNode.style.position === 'relative';
        el.style.top = (rel ? drag.y + dy : Math.max(0, drag.y + dy)) + 'px';
      }
    });
    document.addEventListener('pointerup', function(){
      if (!drag) return;
      var wasResize = drag.resize;
      drag = null;
      var k = 1 / pxPerMm();
      var msg = { type: 'pp-overlay-change', id: el.dataset.ovId,
        xMm: el.offsetLeft * k, yMm: el.offsetTop * k,
        wMm: el.offsetWidth * k, hMm: el.offsetHeight * k };
      // 不是「固定页顶」的章：放下时挂到它中线所在的那个顶层区块上，y 改成距区块顶边。
      // 用 getBoundingClientRect 的差值、按宿主实际宽度换算成 mm，与 body 的 zoom 无关
      var lay = el.parentNode;
      var follows = lay && (lay.classList.contains('pp-ov-layer-end') || lay.classList.contains('pp-ov-anchor'));
      var anchors = document.querySelectorAll('.pp-ov-anchor');
      if (follows && anchors.length && !wasResize) {
        var r = el.getBoundingClientRect();
        var cy = r.top + r.height / 2;
        var best = anchors[0];
        Array.prototype.forEach.call(anchors, function(a){ if (a.getBoundingClientRect().top <= cy) best = a; });
        var br = best.getBoundingClientRect();
        var mmPerVisPx = parseFloat(host.dataset.contentWmm) / host.getBoundingClientRect().width;
        var xMm = (r.left - br.left) * mmPerVisPx, yMm = (r.top - br.top) * mmPerVisPx;
        best.appendChild(el);
        el.style.left = (xMm * pxPerMm()) + 'px';
        el.style.top = (yMm * pxPerMm()) + 'px';
        msg.xMm = xMm; msg.yMm = yMm;
        msg.anchor = 'section'; msg.anchorSectionId = best.dataset.anchorSec;
      }
      parent.postMessage(msg, '*');
    });
  });
})();</script>`;

export function buildContractHtml(
  template: ContractTemplate,
  data: ContractRenderData,
  mode: RenderMode = 'output',
): string {
  const s = template.settings;
  const PAPER: Record<string, [number, number]> = { A4: [210, 297], A5: [148, 210], A3: [297, 420], Letter: [216, 279] };
  const [pw, ph] = PAPER[s.paperSize];
  const paperWmm = s.orientation === 'portrait' ? pw : ph;
  const paperHmm = s.orientation === 'portrait' ? ph : pw;
  // 屏幕预览/编辑：body 锁成真实纸张宽度（1mm = 3.78px 恒定），overlay 的
  // mm 坐标与页面比例尺完全一致，拖拽换算才不会漂。output 模式交给 @page。
  const screenPageCss =
    mode === 'output'
      ? ''
      : `body { width: ${paperWmm}mm; min-height: ${paperHmm}mm; margin: 0 auto; }`;
  // @page margin:0 = 关闭浏览器打印页眉/页脚；页边距由 printPage 骨架补回
  const css = `
    @page { size: ${s.paperSize} ${s.orientation}; margin: 0; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Helvetica Neue', Arial, sans-serif; color: #000; }
    body { padding: ${s.marginMm}mm; font-size: ${s.baseFontSizePx}px; line-height: 1.5; }
    ${screenPageCss}
    table { border-collapse: collapse; }
    th, td { border: 1px solid #999; padding: 4px 6px; vertical-align: top; word-break: break-word; }
    /* 落款是「标签 + 下划线」，不是格子：全局框线别套到它身上（下划线是行内样式，不受影响） */
    .pp-sig td { border: none; }
    tr { page-break-inside: avoid; }
    ${printPageChromeCss(s.marginMm)}
  `;

  // 编辑态给每个区块套可点选壳（画布点选 ↔ 面板联动）。
  // 条件显隐：output/preview 模式下不满足规则的区块直接不渲染；edit 模式保留
  // （灰显）以便用户编辑，避免"配了条件就从画布消失没法改"。
  const inner = buildInnerHost(template, data, mode, paperWmm, paperHmm);

  return `<!doctype html>
<html lang="zh-CN">
<head>
<meta charset="utf-8" />
<title>${esc(template.name)}</title>
<style>${css}</style>
</head>
<body${
    mode === 'edit'
      ? ` data-blocks="${esc(JSON.stringify(SLASH_BLOCKS))}" data-fields="${esc(
          JSON.stringify(data.mainFieldMetas.map((f) => f.name)),
        )}"`
      : ''
  }>
${wrapPrintBody(inner, { headerText: s.headerText, footerText: s.footerText })}
${mode === 'edit' ? OVERLAY_EDIT_SCRIPT : mode === 'preview' ? ZOOM_FIT_SCRIPT : ''}
</body>
</html>`;
}

/** 单条记录的正文宿主（含区块/水印/自由元素）。批量渲染复用它，每条一页。 */
function buildInnerHost(
  template: ContractTemplate,
  rawData: ContractRenderData,
  mode: RenderMode,
  paperWmm: number,
  paperHmm: number,
): string {
  const s = template.settings;
  // 成品（打印/导出/签署）永远按真实数据渲染，占位符只活在预览/编辑画布里。
  // 字段胶囊更要卡死在编辑态：万一它漏进成品，客户的合同上印的就是
  // 「{{客户名称}}」而不是客户名 —— 这种事故不能靠调用方自觉。
  const data =
    mode === 'edit'
      ? rawData
      : {
          ...rawData,
          fieldChips: false,
          ...(mode === 'output' && rawData.sampleMode ? { sampleMode: false } : {}),
        };
  /**
   * 渲染一层区块（顶层，或分栏的某一栏）。编辑态给每个区块套 .pp-sec 壳、
   * 缝隙里放插入点；分栏里的区块同样套壳，画布上一样能点、能拖、能双击。
   */
  const overlayLayers = renderOverlays(template, mode);
  const renderList = (list: ContractSection[], parentId: string | null, col: number): string => {
    const insAttr = parentId ? ` data-parent="${esc(parentId)}" data-col="${col}"` : '';
    const out = list
      .map((sec, secIndex) => {
        const rule = template.visibility?.[sec.id];
        const visible = !rule || evalVisibility(rule, data);
        // 只有顶层区块能挂章；被条件藏掉的区块照样留挂靠层，章停在它原本的位置
        const anchor = parentId ? '' : sectionAnchorLayer(sec.id, overlayLayers.bySection[sec.id], mode);
        if (!visible && mode !== 'edit') return anchor;
        const body = sec.type === 'columns' ? renderColumnsSection(sec) : renderSection(sec, data, mode);
        if (mode === 'edit') {
          // 被条件藏掉的区块在画布上要说清楚：光变淡 40% 用户看不出来，
          // 会以为是数据没带出来
          const dim = !visible ? 'opacity:0.4;' : '';
          const badge = !visible
            ? '<div style="font-size:11px;color:#c26442;background:#fbeee9;border:1px dashed #c26442;border-radius:3px;padding:2px 6px;margin-bottom:4px;display:inline-block;">此区块在当前记录下按「条件显示」隐藏，打印/预览时不会出现</div>'
            : '';
          // 画布上双击就能改文字。带的是**原始内容**（{{字段名}} 还在），
          // 不是渲染后的值 —— 直接编辑渲染值会把字段绑定改成写死的文本。
          const raw =
            sec.type === 'header' || sec.type === 'text_block'
              ? ` data-edit="text" data-raw="${esc(sec.content ?? '')}"`
              : '';
          // 还没填内容的区块渲染出来几乎是一条细线，画布看着像空的、像坏了。
          // 编辑态给个明确的占位，用户才知道这儿有个块、该往里填什么。
          const shown =
            sec.type !== 'columns' && isBlankBody(body)
              ? `<div style="border:1px dashed var(--pp-ph,#d8cfc4);border-radius:6px;padding:10px 12px;` +
                `color:#a89f94;font-size:12px;background:rgba(0,0,0,0.015);">` +
                `${esc(SECTION_TYPE_LABELS[sec.type] ?? sec.type)}（还没有内容，点这里在右侧属性里填）</div>`
              : body;
          // 每个区块前面留一个插入点，最后再补一个 —— 用户可以在任意位置插块，
          // 而不是只能追加到末尾再一路点「上移」
          const ins = `<div class="pp-ins" data-ins-at="${secIndex}"${insAttr}></div>`;
          const tail =
            secIndex === list.length - 1 ? `<div class="pp-ins" data-ins-at="${list.length}"${insAttr}></div>` : '';
          return `${ins}${anchor}<div class="pp-sec" data-sec-id="${esc(sec.id)}"${raw} style="${dim}">${badge}${shown}</div>${tail}`;
        }
        return anchor + body;
      })
      .join('\n');
    // 空栏：编辑态也得有个落点，否则这一栏永远放不进东西
    if (mode === 'edit' && list.length === 0 && parentId) {
      return `<div class="pp-ins pp-ins-empty" data-ins-at="0"${insAttr}></div>`;
    }
    return out;
  };

  // 分栏 = 一张无框表，每格里递归渲染那一栏的区块。用表格而不是 flex：
  // 打印引擎对表格的分页最稳。
  const renderColumnsSection = (sec: ColumnsSection): string => {
    const n = Math.max(1, sec.columns.length);
    const widths = sec.widths.length === n ? sec.widths : Array.from({ length: n }, () => 100 / n);
    const gap = Number(sec.gapMm ?? 4);
    const tds = sec.columns
      .map((col, i) => {
        const pad = i < n - 1 ? `padding-right:${gap}mm;` : '';
        const minH = mode === 'edit' ? 'min-height:24px;' : '';
        return `<td style="width:${widths[i]}%;vertical-align:top;${pad}"><div style="${minH}">${renderList(col, sec.id, i)}</div></td>`;
      })
      .join('');
    return `<table style="width:100%;border-collapse:collapse;table-layout:fixed;margin-bottom:8px;"><tr>${tds}</tr></table>`;
  };

  const sectionsHtml = renderList(template.sections, null, -1);

  const contentWmm = paperWmm - s.marginMm * 2;
  const hostMinH = mode === 'output' ? '' : `min-height:${paperHmm - s.marginMm * 2}mm;`;
  const wm = (s.watermarkText || '').trim();
  const wmImg = (s.watermarkImage || '').trim();
  // 水印用可平铺的背景图，而不是固定数量的 span。以前铺 60 个，A4 宽度下
  // 只够盖一页半 —— 一份十几页的报价单，第 3 页往后完全没有「作废/副本」字样，
  // 恰恰是水印最该起作用的地方。
  const textWm = wm ? `<div style="position:absolute;inset:0;pointer-events:none;z-index:1;${tiledWatermarkCss(wm)}"></div>` : '';
  // 图片水印同样不能只在整份文档正中放一张：十几页的文档它会落在第 7 页中间，
  // 其余页一片空白。按「一页正文高」切成一格一格，每格居中放一张；多备 40 格
  // 由 overflow:hidden 裁掉，比 JS 量页数稳（成品截图时不跑脚本）。
  const pageInnerHmm = paperHmm - s.marginMm * 2;
  const imgWm = wmImg
    ? `<div style="position:absolute;inset:0;overflow:hidden;pointer-events:none;z-index:1;display:grid;grid-auto-rows:${pageInnerHmm}mm;">${Array.from(
        { length: 40 },
        () =>
          `<div style="display:flex;align-items:center;justify-content:center;"><img src="${esc(wmImg)}" alt="" style="max-width:55%;max-height:55%;opacity:0.08;object-fit:contain;" /></div>`,
      ).join('')}</div>`
    : '';
  const watermark = imgWm + textWm;

  // 对照底图只垫在编辑画布下面，成品里一定不能出现
  const underlay =
    mode === 'edit' && data.underlay?.dataUrl
      ? `<div style="position:absolute;inset:0;z-index:0;pointer-events:none;background-image:url('${esc(
          data.underlay.dataUrl,
        )}');background-size:100% auto;background-repeat:no-repeat;background-position:top center;opacity:${
          Number(data.underlay.opacity) || 0.35
        };"></div>`
      : '';
  return `<div class="pp-ov-host" data-content-wmm="${contentWmm}" style="position:relative;${hostMinH}">
${underlay}
${watermark}
<div class="pp-flow" style="position:relative;z-index:2;">${sectionsHtml}${overlayLayers.end}${
    mode === 'edit'
      ? '<div class="pp-tail" contenteditable="plaintext-only" data-ph="点这里输入文字，或敲 / 插入块" spellcheck="false"></div>'
      : ''
  }</div>
${overlayLayers.page}
</div>`;
}

/**
 * 批量成品 HTML：多条记录各自成页（page-break-before），一次打印/导出。
 * 只用于 output（打印/PDF），不含编辑/预览脚本。
 */
export function buildBatchHtml(template: ContractTemplate, dataList: ContractRenderData[]): string {
  const s = template.settings;
  const PAPER: Record<string, [number, number]> = { A4: [210, 297], A5: [148, 210], A3: [297, 420], Letter: [216, 279] };
  const [pw, ph] = PAPER[s.paperSize];
  const paperWmm = s.orientation === 'portrait' ? pw : ph;
  const paperHmm = s.orientation === 'portrait' ? ph : pw;
  const css = `
    @page { size: ${s.paperSize} ${s.orientation}; margin: 0; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Helvetica Neue', Arial, sans-serif; color: #000; }
    body { padding: ${s.marginMm}mm; font-size: ${s.baseFontSizePx}px; line-height: 1.5; }
    table { border-collapse: collapse; }
    th, td { border: 1px solid #999; padding: 4px 6px; vertical-align: top; word-break: break-word; }
    /* 落款是「标签 + 下划线」，不是格子：全局框线别套到它身上（下划线是行内样式，不受影响） */
    .pp-sig td { border: none; }
    tr { page-break-inside: avoid; }
    .pp-rec { page-break-before: always; }
    .pp-rec:first-child { page-break-before: avoid; }
    ${printPageChromeCss(s.marginMm)}
  `;
  const recs = dataList
    .map((data) => `<div class="pp-rec">${buildInnerHost(template, data, 'output', paperWmm, paperHmm)}</div>`)
    .join('\n');
  return `<!doctype html>
<html lang="zh-CN">
<head><meta charset="utf-8" /><title>${esc(template.name)}（批量 ${dataList.length} 条）</title><style>${css}</style></head>
<body>
${wrapPrintBody(recs, { headerText: s.headerText, footerText: s.footerText })}
</body>
</html>`;
}
